import { parseAsString, useQueryState } from "nuqs";
import { FinishEnum, GameCard, NONFOIL_FINISH } from "modules/cards/model/card";
import { useMemo, useCallback } from "react";
import {
  finishSpec,
  primaryAvailableFinish,
} from "modules/cards/model/finishBehavior";

interface UseCardFinishStateProps {
  card: GameCard;
}

interface UseCardFinishStateReturn {
  selectedFinish: FinishEnum;
  apiFinish: string;
  setFinish: (finish: FinishEnum) => void;
}

/**
 * Shared finish selection for card images, prices, and charts. The selected
 * FinishEnum is synchronized through the URL so every consumer uses the same
 * exact printing surface.
 */
export function useCardFinishState({
  card,
}: UseCardFinishStateProps): UseCardFinishStateReturn {
  const [finishParam, setFinishParam] = useQueryState(
    "finish",
    parseAsString.withDefault("").withOptions({ shallow: true })
  );

  const selectedFinish = useMemo(() => {
    if (finishParam) {
      const match = finishSpec(finishParam);
      if (match && card.finishes.includes(match.value as FinishEnum)) {
        return match.value as FinishEnum;
      }
    }

    return primaryAvailableFinish(card.finishes) ?? NONFOIL_FINISH;
  }, [finishParam, card.finishes]);

  const apiFinish = selectedFinish === NONFOIL_FINISH ? "" : selectedFinish;

  const setFinish = useCallback(
    (finish: FinishEnum) => {
      if (!card.finishes.includes(finish)) return;
      setFinishParam(finish === NONFOIL_FINISH ? "" : finish);
    },
    [card.finishes, setFinishParam]
  );

  return { selectedFinish, apiFinish, setFinish };
}
